import { DotLottie, DotLottieReact } from '@lottiefiles/dotlottie-react';
import { useRef } from 'react';

import { ContentCard } from '@flipster/contentful-graphql/codegen/types.generated';
import { IsomorphicLink } from '@flipster/react/design-utils/isomorphic-link';
import { Picture } from '@flipster/react/design-utils/picture';
import ArrowRightIcon from '@flipster/react/icon/generated/ArrowRightIcon';
import { css } from '@flipster/styled-system/css';
import { circle, flex, grid, hstack, linkOverlay, stack } from '@flipster/styled-system/patterns';

interface Props {
  cards?: (ContentCard | null)[];
}

export const ContentCards = ({ cards }: Props) => {
  const lottiesRef = useRef<(DotLottie | null)[]>([null, null, null]);

  if (!cards || cards.length === 0) return null;

  return (
    <div
      className={grid({
        columns: { base: 1, tablet: cards.length },
        // gridAutoRows: '1fr', // 높이가 다르게 보여줘야하는 경우가 더 많아서 일단 주석
        gap: { base: 16, tablet: 24 },
        my: { base: 24, tablet: 48 },
      })}
    >
      {cards.filter(Boolean).map((card, index) => {
        if (!card) return null;

        const isLink = !!card.url;
        return (
          <div
            key={card.sys.id}
            className={css(
              stack.raw({
                pos: 'relative',
                p: { base: 20, tablet: 32 },
                bg: 'background.default.level-1',
                border: '1px solid transparent',
                rounded: 16,
              }),
              isLink && {
                tabletDown: {
                  borderColor: 'border.default.level-3',
                },
                tablet: {
                  _hover: {
                    transform: 'scale(1.02)',
                    transition: 'transform 0.2s ease',
                    borderColor: 'border.default.level-3',
                    '& [data-arrow-icon]': {
                      color: 'content.default.level-5',
                      bgColor: 'background.accent.primary',
                      transform: 'rotate(-45deg)',
                    },
                  },
                },
              },
            )}
            onMouseEnter={() => {
              lottiesRef.current[index]?.setMode('forward');
              lottiesRef.current[index]?.setSpeed(1);
              lottiesRef.current[index]?.play();
            }}
            onMouseLeave={() => {
              lottiesRef.current[index]?.setMode('reverse');
              lottiesRef.current[index]?.setSpeed(2); // 2배 빠른 속도로 역재생
              lottiesRef.current[index]?.play();
            }}
          >
            {!isLink && !card.icon && (
              <div
                className={circle({
                  w: { base: 40, tablet: 60 },
                  h: { base: 40, tablet: 60 },
                  textStyle: 'Display/L',
                  color: 'inverse.white',
                  textAlign: 'center',
                  bg: 'background.accent.primary',
                })}
              >
                {index + 1}
              </div>
            )}
            {card.icon?.url && (
              <img
                src={card.icon.url}
                alt={card.icon.title || 'card icon'}
                className={css({
                  w: { base: 50, tablet: 70 },
                  h: { base: 50, tablet: 70 },
                  mb: 0,
                })}
              />
            )}
            <h4
              className={css(
                hstack.raw({ textStyle: 'Title/L', color: 'content.default.level-5' }),
                isLink && { mb: { base: 16, tablet: 10 } },
                !isLink && { my: { base: 16, tablet: 24 } },
              )}
            >
              <p className={css({ flex: 1 })}>{card.title}</p>
              {isLink && (
                <div
                  data-arrow-icon
                  className={css(
                    circle.raw({
                      size: 50,
                      color: 'content.default.level-1',
                      bgColor: 'background.default.level-3',
                      transition: 'all 0.2s ease',
                    }),
                    { display: { base: 'none', tablet: 'flex' } },
                  )}
                >
                  <ArrowRightIcon size={24} />
                </div>
              )}
            </h4>
            <p className={css({ textStyle: 'Text/L/Regular', color: 'content.default.level-3' })}>{card.description}</p>
            {card.mobileImage?.url && (
              <div
                className={css(
                  flex.raw({ align: 'center', flex: 1 }),
                  !!card.desktopLottie && { display: { tablet: 'none' } },
                )}
              >
                <Picture
                  src={{
                    base: card.mobileImage.url,
                    tablet: card.desktopImage?.url,
                  }}
                  alt={card.title || 'card image'}
                  css={{ w: '100%', h: 'auto', mt: { base: 16, tablet: 24 }, mb: 0 }}
                />
              </div>
            )}
            {card.desktopLottie?.url && (
              <div className={css(flex.raw({ display: { base: 'none', tablet: 'flex' }, align: 'center', flex: 1 }))}>
                <DotLottieReact
                  src={card.desktopLottie.url}
                  className={css({
                    w: '100%',
                    h: 'auto',
                    mt: { base: 16, tablet: 24 },
                  })}
                  dotLottieRefCallback={(lotteryRef) => {
                    lottiesRef.current[index] = lotteryRef;
                  }}
                />
              </div>
            )}
            {card.url && <IsomorphicLink href={card.url} newTab className={linkOverlay()} />}
          </div>
        );
      })}
    </div>
  );
};
